import { useCallback } from 'react';
import type { CurrencyConverterOptions } from '@apps/card/hooks/useCurrencyConverter';
import { useCurrencyConverter } from '@apps/card/hooks/useCurrencyConverter';
import type { MinorUnitsToPriceStringOptions } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useOrderPriceConversion } from '@apps/card/components/Price/OrderPriceConversion';

export type FormatOptions = CurrencyConverterOptions &
  MinorUnitsToPriceStringOptions &
  Readonly<{
    /**
     * False for an amount already in the target currency — an order's tax, or a total built from
     * converted figures. It is still labelled with the order's currency; only the rate is skipped.
     */
    convert?: boolean;
  }>;

export const useFormatPriceString = () => {
  const convertAmount = useCurrencyConverter();

  const minorUnitsToPriceString = useMinorUnitsToPriceString();

  // `useCurrencyConverter` already applies an order's committed rate; the currency it was billed
  // in has to be carried through to formatting too, or the amount converts one way and gets
  // labelled another.
  const orderConversion = useOrderPriceConversion();

  return useCallback(
    (amountInMinorUnits: number, { convert = true, ...options }: FormatOptions = {}) => {
      const convertedAmount = convert
        ? convertAmount(amountInMinorUnits, {
            precision: options.precision,
            currency: options.currency,
          })
        : amountInMinorUnits;

      // A placed order's receipt names its currency on every row (CA$, A$), matching its tax and
      // total rows; 'short' would print a bare "$" that reads as USD.
      const form = options.form ?? (orderConversion ? undefined : 'short');
      const currency = options.currency ?? orderConversion?.currency;

      return minorUnitsToPriceString(convertedAmount, { ...options, currency, form });
    },
    [convertAmount, minorUnitsToPriceString, orderConversion],
  );
};
